/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { Box, useMediaQuery } from "@mui/material";
import { css } from "aphrodite";
import Typography from "../../../components/common/new/Typography/Typography";
import Dashboard from "../../../services/api/dashboard";
import "./subscriptioncounter.styles.css";
import Style from "./SubscriptionCounter.styles";

// Component definitions outside render
function SmallCard({
  label,
  value,
  bgColor,
  textColor,
  isLast = false,
  isMobile,
}) {
  return (
    <Box
      style={{
        background: bgColor,
        borderRadius: isMobile ? "8px" : "10px",
        padding: isMobile ? "8px 4px" : "12px 8px",
        textAlign: "center",
        minWidth: isMobile ? "0" : isLast ? "70px" : "60px",
        maxWidth: isMobile ? "none" : "auto",
        border: "1px solid rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: isMobile ? "50px" : "65px",
        flex: isMobile ? "1 1 0" : "0 0 auto",
        boxSizing: "border-box",
      }}
    >
      <Typography
        style={{
          fontSize: isMobile ? "8px" : "10px",
          fontWeight: 500,
          color: "#666",
          marginBottom: isMobile ? "3px" : "6px",
          lineHeight: "1.2",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <Typography
        style={{
          fontSize: isMobile ? "12px" : "15px",
          fontWeight: 700,
          color: textColor,
          lineHeight: "1",
        }}
      >
        {value || 0}
      </Typography>
    </Box>
  );
}

function PlusSymbol({ isMobile }) {
  return (
    <Box
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: isMobile ? "12px" : "16px",
        fontWeight: 600,
        color: "#999",
        margin: "0",
        padding: isMobile ? "0 2px" : "0 4px",
        flexShrink: 0,
      }}
    >
      +
    </Box>
  );
}

function EqualSymbol({ isMobile }) {
  return (
    <Box
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: isMobile ? "12px" : "16px",
        fontWeight: 600,
        color: "#999",
        margin: "0",
        padding: isMobile ? "0 2px" : "0 4px",
        flexShrink: 0,
      }}
    >
      =
    </Box>
  );
}

function SubscriptionCounter() {
  const [vcData, setVcData] = useState(null);
  const [fdtData, setFdtData] = useState(null);
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:768px)");

  // Fetch VC data
  const subscriptionCounterVC = async () => {
    try {
      const response = await Dashboard.getSubscriptionReportVC({ type: 1 }); // VC
      setVcData(response.data || {});
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  // Fetch FDT data
  const subscriptionCounterFDT = async () => {
    try {
      const response = await Dashboard.getSubscriptionReportVC({ type: 3 }); // FDT
      setFdtData(response.data || {});
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    subscriptionCounterVC();
    subscriptionCounterFDT();
  }, []);

  // Get "TOTAL" row from response
  const vcTotalRow = vcData || {};
  const fdtTotalRow = fdtData || {};

  return (
    <Box
      className={css(Style.subscriptionContainer)}
      style={{
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* Value Creation Subscription */}
      <Box
        style={{
          background: "#fff",
          borderRadius: isMobile ? "12px" : "14px",
          padding: isMobile ? "10px 8px" : isTablet ? "14px" : "16px",
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Typography
          size={isMobile ? "13px" : "15px"}
          weight={600}
          color="#333"
          style={{ marginBottom: isMobile ? "8px" : "0" }}
        >
          Value Creation Subscription
        </Typography>
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: isMobile ? "10px" : "16px",
            width: "100%",
            maxWidth: "100%",
            gap: isMobile ? "2px" : "4px",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <SmallCard
            label="Current"
            value={vcTotalRow.inProgressSubscriptions}
            bgColor="rgba(218, 231, 254, 0.4)"
            textColor="#2556A3"
            isMobile={isMobile}
          />
          <PlusSymbol isMobile={isMobile} />
          <SmallCard
            label="New"
            value={vcTotalRow.newSubscriptions}
            bgColor="rgba(255, 239, 194, 0.4)"
            textColor="#FBB902"
            isMobile={isMobile}
          />
          <PlusSymbol isMobile={isMobile} />
          <SmallCard
            label="Renewals"
            value={vcTotalRow.renewedSubscriptions}
            bgColor="rgba(218, 243, 249, 0.4)"
            textColor="#00CFFF"
            isMobile={isMobile}
          />
          <EqualSymbol isMobile={isMobile} />
          <SmallCard
            label="Total"
            value={vcTotalRow.subscriptionCount}
            bgColor="rgba(254, 224, 214, 0.4)"
            textColor="#F88D69"
            isLast
            isMobile={isMobile}
          />
        </Box>
      </Box>

      {/* Future Division Times */}
      <Box
        style={{
          background: "#fff",
          borderRadius: isMobile ? "12px" : "14px",
          padding: isMobile ? "10px 8px" : isTablet ? "14px" : "16px",
          marginTop: isMobile ? "12px" : "20px",
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Typography
          size={isMobile ? "13px" : "15px"}
          weight={600}
          color="#333"
          style={{ marginBottom: isMobile ? "8px" : "0" }}
        >
          Soka Successors
        </Typography>
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: isMobile ? "10px" : "16px",
            width: "100%",
            maxWidth: "100%",
            gap: isMobile ? "2px" : "4px",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <SmallCard
            label="Current"
            value={fdtTotalRow.inProgressSubscriptions}
            bgColor="rgba(218, 231, 254, 0.4)"
            textColor="#2556A3"
            isMobile={isMobile}
          />
          <PlusSymbol isMobile={isMobile} />
          <SmallCard
            label="New"
            value={fdtTotalRow.newSubscriptions}
            bgColor="rgba(255, 239, 194, 0.4)"
            textColor="#FBB902"
            isMobile={isMobile}
          />
          <PlusSymbol isMobile={isMobile} />
          <SmallCard
            label="Renewals"
            value={fdtTotalRow.renewedSubscriptions}
            bgColor="rgba(218, 243, 249, 0.4)"
            textColor="#00CFFF"
            isMobile={isMobile}
          />
          <EqualSymbol isMobile={isMobile} />
          <SmallCard
            label="Total"
            value={fdtTotalRow.subscriptionCount}
            bgColor="rgba(254, 224, 214, 0.4)"
            textColor="#F88D69"
            isLast
            isMobile={isMobile}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default SubscriptionCounter;
